import { useState } from "react";
import { api } from "@/lib/api";
import AdvisorRolodex from "@/components/AdvisorRolodex";

// Homepage refresh (Feb 2026) — matches gauntlet-homepage-mockup.html + copy.
// The Panel section (rolodex) below KEEPS its existing content and layout.

const RINGS_1_TO_3 = [
  {
    n: 1,
    color: "#16a34a",
    title: "Concept & Viability",
    engage: "Socratic Advisor + Pragmatic Advisor",
    achieve: "A validated core concept: proof your idea solves a real problem people will pay for.",
    imperative: "Say what you do in one sentence and have it survive being questioned.",
    kicker: "Clarity as proof — not \u201Cis this a good idea\u201D but \u201Cdo you actually know what it is?\u201D",
  },
  {
    n: 2,
    color: "#dc2626",
    title: "Marketing & Financial Practicality",
    engage: "Skeptical VC + Pragmatic Advisor + Cashflow-Focused CFO",
    achieve: "A financially sound go-to-market plan that survives scrutiny from money people.",
    imperative: "Know your numbers well enough to defend them without opening a spreadsheet.",
    kicker: "Arithmetic as credibility — not \u201Cwill this make money\u201D but \u201Ccan you show your work?\u201D",
  },
  {
    n: 3,
    color: "#7c3aed",
    title: "Deep Trench Obstacles",
    engage: "Director Tactician + Lean VP of Infrastructure + Now-Invested VC",
    achieve: "An operational plan that can actually be executed and scaled without breaking.",
    imperative: "Name the thing most likely to kill this before someone else does.",
    kicker: "Foresight as advantage — not \u201Cwhat could go wrong\u201D but \u201Cwhat\u2019s your answer when it does?\u201D",
  },
];

const RING_4 = {
  n: 4,
  color: "#0f1b33",
  title: "Growth Architecture & Shark Panel",
  engage: "10-persona investor panel — 5 Angel + 5 Seed",
  achieve: "A pitch-tested growth architecture, ready to raise capital and go to market with confidence.",
  imperative: "Walk into an investor meeting and already have every question they\u2019ll ask answered, memorized, cold.",
  kicker: "Investability as infrastructure — not \u201Cis this fundable\u201D but \u201Ccan you answer for it?\u201D",
};

// Ring 5 name intentionally left as a GAP per copy doc. The imperative +
// kicker are the only content on this card, per the mockup instruction:
// "Short card. No 'You engage with' / 'You achieve' lines. Goal statement stands alone."
const RING_5 = {
  n: 5,
  color: "#334155",
  title: "Your Partner",
  imperative: "Pick the investor you actually want and build backward from what they need.",
  kicker: "Specificity as strategy — not \u201Cwho might fund this\u201D but \u201Cwhat would it take for them to say yes?\u201D",
};

function RingLabel({ n, color }) {
  return (
    <div
      className="mono-caps text-[0.7rem] tracking-widest mb-2"
      style={{ color }}
      data-testid={`ring-label-${n}`}
    >
      Ring {n}
    </div>
  );
}

function RingCard({ ring, minimal = false }) {
  return (
    <div
      className="bg-white rounded-xl p-6 sm:p-7 border border-slate-200 flex flex-col gap-4"
      style={{ borderLeft: `4px solid ${ring.color}` }}
      data-testid={`ring-card-${ring.n}`}
    >
      <div>
        <RingLabel n={ring.n} color={ring.color} />
        <h3 className="font-display text-xl sm:text-2xl leading-tight" style={{ color: "#0f1b33" }}>
          {ring.title}
        </h3>
      </div>
      {!minimal && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <div className="mono-caps mb-2" style={{ color: ring.color }}>You engage with</div>
            <div className="text-slate-700 text-sm leading-relaxed">{ring.engage}</div>
          </div>
          <div>
            <div className="mono-caps mb-2" style={{ color: ring.color }}>You achieve</div>
            <div className="text-slate-700 text-sm leading-relaxed">{ring.achieve}</div>
          </div>
        </div>
      )}
      <div className="pt-1">
        <div
          className="font-semibold text-base sm:text-lg leading-snug"
          style={{ color: "#0f1b33" }}
          data-testid={`ring-imperative-${ring.n}`}
        >
          {ring.imperative}
        </div>
        <div
          className="text-slate-500 text-xs sm:text-sm mt-2 leading-relaxed"
          data-testid={`ring-kicker-${ring.n}`}
        >
          {ring.kicker}
        </div>
      </div>
    </div>
  );
}

function GroupHeader({ eyebrow, title, testid }) {
  return (
    <div className="mt-20 lg:mt-24 mb-6" data-testid={testid}>
      <div className="mono-caps text-slate-500 mb-2">{eyebrow}</div>
      <h2 className="font-display text-3xl sm:text-4xl" style={{ color: "#0f1b33" }}>{title}</h2>
    </div>
  );
}

function MarketDivider() {
  return (
    <div className="my-12 sm:my-16" data-testid="divider-go-to-market">
      <div className="text-center text-2xl sm:text-3xl" style={{ color: "#ea580c" }}>▼</div>
      <div className="mt-4 text-center">
        <h3
          className="font-display text-2xl sm:text-3xl leading-tight"
          style={{ color: "#0f1b33" }}
          data-testid="market-divider-title"
        >
          You Can Go to Market After Ring 3
        </h3>
        <p className="mt-4 text-slate-700 max-w-3xl mx-auto text-base leading-relaxed">
          Graduating Ring 3 gives your business a go / no-go decision — and the tools to act on it.
        </p>
        <p className="mt-3 text-slate-700 max-w-3xl mx-auto text-base leading-relaxed">
          You leave with a plan to win day one and the infrastructure to build a sellable business:
          an overview brief, an investor overview, a slide deck —
        </p>
        <div className="mt-6 mx-auto max-w-3xl">
          <div className="h-px bg-slate-300" />
          <p
            className="mt-6 font-semibold text-lg sm:text-xl leading-snug"
            style={{ color: "#0f1b33" }}
            data-testid="market-divider-differentiator"
          >
            and a written record of every obstacle you cleared, and how.
          </p>
        </div>
      </div>
      <div className="mt-6 text-center text-2xl sm:text-3xl" style={{ color: "#ea580c" }}>▼</div>
    </div>
  );
}

function FundingDivider() {
  return (
    <div className="my-12 sm:my-16 flex items-center justify-center gap-4" data-testid="divider-go-for-funding">
      <span style={{ color: "#ea580c" }} className="text-2xl sm:text-3xl">✦</span>
      <div className="font-bold text-lg sm:text-xl tracking-tight" style={{ color: "#0f1b33" }}>
        You Can Now Go for Funding
      </div>
      <span style={{ color: "#ea580c" }} className="text-2xl sm:text-3xl">✦</span>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post("/auth/request-link", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn\u2019t send the link, try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen surface-tint">
      <div className="relative z-10 max-w-[1120px] mx-auto px-6 sm:px-8 py-12 sm:py-16">
        {/* Header — KEEP */}
        <div className="flex items-center justify-between">
          <div className="mono-caps">A Maloney Industries Product</div>
          <a
            href="/signin"
            className="mono-caps hover:text-slate-900 transition-colors"
            data-testid="home-signin-link"
          >
            Returning? Sign in →
          </a>
        </div>

        {/* Hero */}
        <div className="mt-20 lg:mt-28 max-w-3xl">
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08]"
            style={{ color: "#0f1b33" }}
            data-testid="hero-h1"
          >
            <span className="block">The Gauntlet doesn’t judge ideas.</span>
            <span className="block">It tests whether you can answer for one.</span>
          </h1>

          <p
            className="mt-8 text-lg sm:text-xl text-slate-700 leading-relaxed max-w-2xl"
            data-testid="hero-subhead"
          >
            This is a stress test for anything you’re about to take to market.{" "}
            <span className="font-semibold" style={{ color: "#0f1b33" }}>
              You think you’re ready. You’re not.
            </span>
          </p>

          {/* Login form — KEEP */}
          <div className="mt-10 max-w-md">
            {!sent ? (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="mono-caps block mb-3">Email · magic link login</label>
                  <input
                    data-testid="login-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="founder@example.com"
                    className="input-line"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-600 font-medium" data-testid="login-error">
                    {error}
                  </div>
                )}
                <div className="flex items-center gap-4 flex-wrap">
                  <button
                    type="submit"
                    disabled={loading || !email.trim()}
                    data-testid="login-submit-button"
                    className="cta-orange"
                  >
                    {loading ? "Sending…" : "Send Magic Link →"}
                  </button>
                  <span className="text-slate-500 text-sm italic" data-testid="login-coffee-up">
                    Coffee up and bring your best.
                  </span>
                </div>
              </form>
            ) : (
              <div data-testid="login-sent-message">
                <div className="mono-caps mb-4" style={{ color: "#16a34a" }}>Link dispatched</div>
                <p className="text-slate-700 leading-relaxed">
                  Check <span className="font-semibold text-slate-900">{email}</span> for a single-use link.
                  Expires in 20 minutes.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Section lead-in */}
        <div className="mt-24 lg:mt-32 max-w-3xl" data-testid="rings-section-lead">
          <p className="text-slate-700 text-lg leading-relaxed">
            Four rings of interrogation, run by the people who see the blind sides you missed.
          </p>
          <h2
            className="mt-6 font-display text-2xl sm:text-3xl"
            style={{ color: "#0f1b33" }}
            data-testid="score-to-advance-h2"
          >
            Score an 85 or above to advance.
          </h2>
        </div>

        {/* Group 1 — Rings 1–3 */}
        <GroupHeader eyebrow="Rings 1–3" title="Can you answer for it?" testid="group-header-rings-1-3" />
        <div className="grid gap-5 sm:gap-6" data-testid="rings-1-3-grid">
          {RINGS_1_TO_3.map((r) => <RingCard key={`r-${r.n}`} ring={r} />)}
        </div>

        {/* Divider — go to market */}
        <MarketDivider />

        {/* Group 2 — Rings 4–5 */}
        <GroupHeader eyebrow="Rings 4–5" title="Can you get funded for it?" testid="group-header-rings-4-5" />
        <div className="grid gap-5 sm:gap-6" data-testid="rings-4-5-grid">
          <RingCard ring={RING_4} />
          <RingCard ring={RING_5} minimal />
        </div>

        {/* Divider — go for funding */}
        <FundingDivider />

        {/* Panel section — KEEP entirely */}
        <AdvisorRolodex />

        {/* Footer — KEEP */}
        <footer className="mt-24 pt-12 border-t border-slate-200" data-testid="site-footer">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <div className="font-bold text-lg" style={{ color: "#0f1b33" }}>Maloney Industries</div>
              <div className="text-sm mt-1" style={{ color: "#ea580c" }}>
                Accelerating Growth for Business Leaders
              </div>
            </div>
            <a
              href="https://maloneyindustries.ai"
              className="mono-caps hover:text-slate-900 transition-colors"
              data-testid="footer-back-link"
            >
              ← Back to maloneyindustries.ai
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
